import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/20/solid';
import map from 'lodash/map';
import { Carousel } from 'react-responsive-carousel';
import { Dialog } from '@headlessui/react';

import { Button, LoadingContainer, TopImage } from '@project/components';
import { useGetCharity } from '@project/queries';
import { concat } from 'lodash';

export const IndividualCharityView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [videoIsOpen, setVideoIsOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState(0);

  const [isOpen, setIsOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(0);

  const { data: charity, isLoading } = useGetCharity(id!);

  const handleIsOpen = (index: number) => {
    setActiveImage(index);
    setVideoIsOpen(false);
    setIsOpen(true);
  };

  const handleOpenVideo = (index: number) => {
    setActiveVideo(index);
    setIsOpen(false);
    setVideoIsOpen(true);
  };

  const handleClose = () => setIsOpen(false);

  const ImageModal = useCallback(
    () => (
      <Dialog
        open={isOpen}
        onClose={handleClose}
        className="fixed inset-10 mx-auto my-auto hidden items-center justify-center md:flex"
      >
        <div className="fixed inset-0 flex overflow-y-auto bg-black/80">
          <div className="m-auto flex items-center justify-center">
            <Dialog.Panel className="h-[400px] w-[500px] overflow-y-auto">
              <Carousel
                autoPlay={false}
                infiniteLoop={false}
                showStatus={false}
                showIndicators={false}
                showThumbs={false}
                showArrows
                selectedItem={activeImage}
              >
                {map(concat(charity?.featureImage, charity?.images), (src, index) => (
                  <img src={src} alt="Expanded image" key={index} />
                ))}
              </Carousel>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    ),
    [isOpen, activeImage],
  );
  const VideoModal = useCallback(
    () => (
      <Dialog
        open={videoIsOpen}
        onClose={() => setVideoIsOpen(false)}
        className="fixed inset-10 mx-auto my-auto hidden items-center justify-center md:flex"
      >
        <div className="fixed inset-0 flex overflow-y-auto bg-black/80">
          <div className="m-auto flex items-center justify-center">
            <Dialog.Panel className="h-[400px] w-[500px] overflow-y-auto">
              <Carousel
                autoPlay={false}
                infiniteLoop={false}
                showStatus={false}
                showIndicators={false}
                showThumbs={false}
                showArrows
                selectedItem={activeVideo}
              >
                {map(charity?.videos, (src, index) => (
                  <video src={src} controls key={index}>
                    Your browser does not support the video tag.
                  </video>
                ))}
              </Carousel>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    ),
    [videoIsOpen, activeVideo],
  );

  const goBack = () => navigate(-1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="mb-20">
      <TopImage />
      <LoadingContainer isLoading={isLoading}>
        <div className="flex flex-col">
          <div className="mt-20 mb-14 md:mx-10 lg:my-14">
            <div className="flex flex-row items-center">
              <Button style="bg-gray-200 rounded md:ml-2 ml-4" onClick={goBack}>
                <ChevronLeftIcon className="w-8" />
              </Button>
              <div className="ml-4 text-xl font-medium underline decoration-gold decoration-2 underline-offset-4 md:text-2xl">
                {charity?.name}
              </div>
            </div>
            <p className="mx-6 mt-20 text-lg leading-loose tracking-wide md:mx-14 md:mt-14 md:text-justify xl:mx-[10%]">
              {charity?.description}
            </p>
          </div>
          <div className="mt-6 flex flex-row flex-wrap justify-center bg-black p-2 md:p-10">
            {map(concat(charity?.featureImage, charity?.images), (item, index) => (
              <div className="m-4 flex flex-row lg:mx-6">
                <img
                  key={index}
                  onClick={() => handleIsOpen(index)}
                  className="w-[300px] border-y-2 border-gold object-cover py-6"
                  src={item}
                />
              </div>
            ))}
            {map(charity?.videos, (item, index) => (
              <div className="m-4 flex flex-row lg:mx-6">
                <video
                  key={index}
                  onClick={() => handleOpenVideo(index)}
                  className="w-[300px] border-y-2 border-gold object-cover py-6"
                  src={item}
                />
              </div>
            ))}
          </div>
          <ImageModal />
          <VideoModal />
        </div>
      </LoadingContainer>
    </div>
  );
};
